import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { FileDropzone } from "@/components/file-dropzone";
import { ArrowLeft, Save, FileText, Loader2, TrendingUp, TrendingDown, Minus, Link2, Wallet, ExternalLink, Archive, ArchiveRestore, Trash2, Files } from "lucide-react";
import { format, parseISO } from "date-fns";
import { formatCurrency } from "@/lib/utils";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Contract {
  id: number;
  uploadedBy: number;
  ownerUserId: number | null;
  title: string;
  referenceNumber: string | null;
  supplierName: string | null;
  supplierContactName: string | null;
  supplierEmail: string | null;
  supplierAccountNumber: string | null;
  additionalSupplierAccountNumber: string | null;
  supplierPhone: string | null;
  startDate: string | null;
  endDate: string | null;
  durationMonths: number | null;
  noticePeriodDays: number | null;
  cost: number | null;
  currency: string | null;
  paymentFrequency: string | null;
  renewalStatus: string;
  paymentMethod: string | null;
  notes: string | null;
  previousContractId: number | null;
  originalFilename: string;
  confirmed: boolean;
  isPrepayment: boolean;
  prepaymentAmount: number | null;
  prepaymentDescription: string | null;
  prepaymentNominalDr: string | null;
  prepaymentNominalCr: string | null;
  createdAt: string;
  archivedAt: string | null;
  archivedBy: number | null;
  archivedReason: string | null;
  alertCcEmail: string | null;
}

interface ContractDocument {
  id: number;
  contractId: number;
  uploadedBy: number;
  originalFilename: string;
  createdAt: string;
}

interface PrepaymentSummary {
  monthsTotal: number;
  monthsReleased: number;
  monthsRemaining: number;
  releasedAmount: number;
  pendingAmount: number;
  remainingAmount: number;
}

interface UserRecord {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface CompareResult {
  current: Contract;
  previous: Contract | null;
  priceChange: { previousCost: number; currentCost: number; delta: number; percentChange: number } | null;
}

const STATUS_OPTIONS = [
  { value: "current", label: "Current contract" },
  { value: "pending", label: "Pending review" },
  { value: "review", label: "Under review" },
  { value: "renewed", label: "Renewed" },
  { value: "not-renewing", label: "Not renewing" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "credit_card", label: "Credit Card" },
  { value: "on_account", label: "On Account" },
];

export default function ContractDetailPage() {
  const [, params] = useRoute("/contracts/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === "admin";
  const id = params ? parseInt(params.id, 10) : null;

  const [form, setForm] = useState<Partial<Contract> | null>(null);

  const { data: contract, isLoading } = useQuery<Contract>({
    queryKey: ["contract", id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/contracts/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!id,
  });

  // Live prepayment summary — only renders when the contract is flagged
  // as a prepayment. Re-fetched after each save so the operator sees the
  // schedule update immediately after editing dates/amount.
  const { data: prepaymentDetail } = useQuery<{ summary: PrepaymentSummary }>({
    queryKey: ["prepayment-detail", id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/prepayments/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!id && !!contract?.isPrepayment,
  });

  const { data: comparison } = useQuery<CompareResult>({
    queryKey: ["contract-compare", id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/contracts/${id}/compare`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!id,
  });

  const { data: users = [] } = useQuery<UserRecord[]>({
    queryKey: ["users-list"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/contracts/owner-options`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: allContracts = [] } = useQuery<Contract[]>({
    queryKey: ["contracts"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/contracts`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: documents = [] } = useQuery<ContractDocument[]>({
    queryKey: ["contract-documents", id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/contracts/${id}/documents`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (contract) setForm(contract);
  }, [contract]);

  const [uploadingDoc, setUploadingDoc] = useState(false);

  const uploadDocMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BASE}/api/contracts/${id}/documents`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-documents", id] });
      toast({ title: "Document added" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteDocMutation = useMutation({
    mutationFn: async (docId: number) => {
      const res = await fetch(`${BASE}/api/contract-documents/${docId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete document");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-documents", id] });
      toast({ title: "Document removed" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (archive: boolean) => {
      const res = await fetch(`${BASE}/api/contracts/${id}/${archive ? "archive" : "unarchive"}`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(archive ? "Failed to archive" : "Failed to restore");
      return res.json();
    },
    onSuccess: (_data, archive) => {
      queryClient.invalidateQueries({ queryKey: ["contract", id] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast({ title: archive ? "Contract archived" : "Contract restored to live" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const handleDocFiles = async (files: File[]) => {
    setUploadingDoc(true);
    for (const file of files) {
      try {
        await uploadDocMutation.mutateAsync(file);
      } catch {}
    }
    setUploadingDoc(false);
  };

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Contract>) => {
      const res = await fetch(`${BASE}/api/contracts/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Update failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract", id] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["prepayment-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["prepayments"] });
      toast({ title: "Contract updated" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const linkPreviousMutation = useMutation({
    mutationFn: async (previousContractId: number | null) => {
      const res = await fetch(`${BASE}/api/contracts/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ previousContractId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to link previous contract");
      }
      return res.json();
    },
    onSuccess: (updated: Contract) => {
      setForm((f) => (f ? { ...f, previousContractId: updated.previousContractId } : f));
      queryClient.invalidateQueries({ queryKey: ["contract", id] });
      queryClient.invalidateQueries({ queryKey: ["contract-compare", id] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast({
        title: updated.previousContractId
          ? "Previous contract linked"
          : "Previous contract cleared",
      });
    },
    onError: (err: any) => {
      setForm((f) => (f ? { ...f, previousContractId: contract?.previousContractId ?? null } : f));
      queryClient.invalidateQueries({ queryKey: ["contract", id] });
      toast({ title: err.message, variant: "destructive" });
    },
  });

  if (isLoading || !form) {
    return (
      <Layout>
        <div className="flex justify-center p-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const setField = (key: keyof Contract, value: any) => setForm((f) => ({ ...(f || {}), [key]: value }));

  const handleSave = () => {
    if (!form) return;
    const payload: any = {
      title: form.title,
      referenceNumber: form.referenceNumber || null,
      supplierName: form.supplierName || null,
      supplierContactName: form.supplierContactName || null,
      supplierEmail: form.supplierEmail || null,
      supplierAccountNumber: form.supplierAccountNumber || null,
      additionalSupplierAccountNumber: form.additionalSupplierAccountNumber || null,
      supplierPhone: form.supplierPhone || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      durationMonths: form.durationMonths || null,
      noticePeriodDays: form.noticePeriodDays || null,
      cost: form.cost ?? null,
      currency: form.currency || "GBP",
      paymentFrequency: form.paymentFrequency || null,
      renewalStatus: form.renewalStatus,
      paymentMethod: form.paymentMethod || null,
      notes: form.notes || null,
      ownerUserId: form.ownerUserId || null,
      alertCcEmail: form.alertCcEmail || null,
      previousContractId: form.previousContractId ?? null,
      confirmed: true,
    };
    payload.isPrepayment = !!form.isPrepayment;
    payload.prepaymentAmount = form.prepaymentAmount ?? null;
    payload.prepaymentDescription = form.prepaymentDescription || null;
    payload.prepaymentNominalDr = form.prepaymentNominalDr || null;
    payload.prepaymentNominalCr = form.prepaymentNominalCr || null;
    updateMutation.mutate(payload);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <button onClick={() => navigate("/contracts")} className="text-sm text-duffells-grey hover:text-primary flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back to contracts
        </button>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-duffells-grey">{form.title}</h1>
            <p className="text-sm text-duffells-grey/60 mt-1">{contract?.originalFilename}</p>
          </div>
          <div className="flex gap-2">
            <a
              href={`${BASE}/api/contracts/${id}/preview`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-slate-50"
            >
              <FileText className="w-4 h-4" />
              View PDF
            </a>
            {form.archivedAt ? (
              <Button
                variant="outline"
                onClick={() => archiveMutation.mutate(false)}
                disabled={archiveMutation.isPending}
              >
                {archiveMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArchiveRestore className="w-4 h-4 mr-2" />}
                Restore to live
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => { if (confirm("Archive this contract? You can restore it later.")) archiveMutation.mutate(true); }}
                disabled={archiveMutation.isPending}
              >
                {archiveMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
                Archive
              </Button>
            )}
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
          </div>
        </div>

        {form.archivedAt && (
          <div className="bg-slate-100 border border-slate-300 rounded-xl p-4 flex items-center gap-3 text-sm text-slate-700">
            <Archive className="w-5 h-5 text-slate-500 shrink-0" />
            <div>
              <span className="font-medium">This contract is archived.</span>{" "}
              Archived on {(() => { try { return format(parseISO(form.archivedAt), "d MMM yyyy, HH:mm"); } catch { return form.archivedAt; } })()}
              {form.archivedReason ? ` — ${form.archivedReason}` : ""}. It's hidden from the live list and excluded from renewal alerts.
            </div>
          </div>
        )}

        {(isAdmin || comparison?.priceChange) && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-semibold text-duffells-grey flex items-center gap-2">
                  Year-on-year comparison
                </h3>
                {comparison?.previous ? (
                  <p className="text-xs text-duffells-grey/60 mt-1">
                    vs previous contract: {comparison.previous.title}
                    {comparison.previous.endDate ? ` (${comparison.previous.endDate})` : ""}
                  </p>
                ) : (
                  <p className="text-xs text-duffells-grey/60 mt-1">
                    No previous contract linked yet
                  </p>
                )}
              </div>
              {comparison?.priceChange && (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xs text-duffells-grey/60">Previous</div>
                    <div className="font-semibold">{formatCurrency(comparison.priceChange.previousCost, form.currency || "GBP")}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-duffells-grey/60">Current</div>
                    <div className="font-semibold">{formatCurrency(comparison.priceChange.currentCost, form.currency || "GBP")}</div>
                  </div>
                  <div className={`flex items-center gap-1 px-3 py-2 rounded-lg font-semibold ${
                    comparison.priceChange.delta > 0
                      ? "bg-red-50 text-red-700"
                      : comparison.priceChange.delta < 0
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-50 text-slate-700"
                  }`}>
                    {comparison.priceChange.delta > 0 ? <TrendingUp className="w-4 h-4" /> : comparison.priceChange.delta < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                    {comparison.priceChange.delta > 0 ? "+" : ""}{comparison.priceChange.percentChange.toFixed(1)}%
                  </div>
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="border-t border-slate-100 pt-4">
                <Label className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Link previous contract
                </Label>
                <p className="text-xs text-duffells-grey/60 mt-1 mb-2">
                  Manually choose which contract to compare against. Useful when supplier names or titles have changed between renewals.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={form.previousContractId ?? ""}
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value, 10) : null;
                      setField("previousContractId", value);
                      linkPreviousMutation.mutate(value);
                    }}
                    disabled={linkPreviousMutation.isPending}
                    className="flex-1 min-w-[260px] px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">— No previous contract —</option>
                    {allContracts
                      .filter((c) => c.id !== id)
                      .map((c) => {
                        const parts = [
                          c.title,
                          c.supplierName ? `· ${c.supplierName}` : "",
                          c.endDate ? `· ends ${c.endDate}` : "",
                        ].filter(Boolean);
                        return (
                          <option key={c.id} value={c.id}>
                            {parts.join(" ")}
                          </option>
                        );
                      })}
                  </select>
                  {linkPreviousMutation.isPending && (
                    <Loader2 className="w-4 h-4 animate-spin text-duffells-grey/60" />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <h3 className="font-semibold text-duffells-grey">Contract details</h3>
            <div>
              <Label>Title</Label>
              <Input value={form.title || ""} onChange={(e) => setField("title", e.target.value)} />
            </div>
            <div>
              <Label>Reference number</Label>
              <Input value={form.referenceNumber || ""} onChange={(e) => setField("referenceNumber", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input type="date" value={form.startDate || ""} onChange={(e) => setField("startDate", e.target.value)} />
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" value={form.endDate || ""} onChange={(e) => setField("endDate", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Duration (months)</Label>
                <Input type="number" value={form.durationMonths ?? ""} onChange={(e) => setField("durationMonths", e.target.value ? parseInt(e.target.value, 10) : null)} />
              </div>
              <div>
                <Label>Payment frequency</Label>
                <Input value={form.paymentFrequency || ""} onChange={(e) => setField("paymentFrequency", e.target.value)} placeholder="monthly / annually" />
              </div>
            </div>
            <div>
              <Label>Notice period to end the contract (days)</Label>
              <Input
                type="number"
                min="0"
                value={form.noticePeriodDays ?? ""}
                onChange={(e) => setField("noticePeriodDays", e.target.value ? parseInt(e.target.value, 10) : null)}
                placeholder="e.g. 30"
              />
              {form.noticePeriodDays && form.endDate ? (
                <p className="text-xs text-duffells-grey/60 mt-1">
                  Notice deadline:{" "}
                  <strong>
                    {(() => {
                      try {
                        const d = parseISO(form.endDate);
                        d.setDate(d.getDate() - form.noticePeriodDays);
                        return format(d, "d MMM yyyy");
                      } catch {
                        return "—";
                      }
                    })()}
                  </strong>
                  . Reminders sent 60, 30 and 10 days before this date.
                </p>
              ) : (
                <p className="text-xs text-duffells-grey/60 mt-1">
                  If set, you'll get extra reminders 60, 30 and 10 days before the cancellation-notice deadline.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cost</Label>
                <Input type="number" step="0.01" value={form.cost ?? ""} onChange={(e) => setField("cost", e.target.value ? parseFloat(e.target.value) : null)} />
              </div>
              <div>
                <Label>Currency</Label>
                <Input value={form.currency || "GBP"} onChange={(e) => setField("currency", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Renewal status</Label>
              <select
                value={form.renewalStatus || "pending"}
                onChange={(e) => setField("renewalStatus", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Payment method</Label>
              <select
                value={form.paymentMethod || ""}
                onChange={(e) => setField("paymentMethod", e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">— Not set —</option>
                {PAYMENT_METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Owner (receives renewal alerts)</Label>
              <p className="text-xs text-duffells-grey/60 mt-0.5 mb-1.5">
                Must be a member of the Finance &amp; Accounts team (users with Contracts access). Only eligible team members are shown below.
              </p>
              <select
                value={form.ownerUserId ?? ""}
                onChange={(e) => setField("ownerUserId", e.target.value ? parseInt(e.target.value, 10) : null)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">— No owner —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Additional alert email (CC)</Label>
              <p className="text-xs text-duffells-grey/60 mt-0.5 mb-1.5">
                Optional. A second address (e.g. a shared inbox) copied on every renewal alert sent to the owner.
              </p>
              <Input
                type="email"
                value={form.alertCcEmail || ""}
                onChange={(e) => setField("alertCcEmail", e.target.value || null)}
                placeholder="e.g. finance-team@duffells.co.uk"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={4} value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <h3 className="font-semibold text-duffells-grey">Supplier</h3>
            <div>
              <Label>Supplier name</Label>
              <Input value={form.supplierName || ""} onChange={(e) => setField("supplierName", e.target.value)} />
            </div>
            <div>
              <Label>Account number</Label>
              <Input value={form.supplierAccountNumber || ""} onChange={(e) => setField("supplierAccountNumber", e.target.value)} />
            </div>
            <div>
              <Label>Additional supplier account number</Label>
              <Input
                value={form.additionalSupplierAccountNumber || ""}
                onChange={(e) => setField("additionalSupplierAccountNumber", e.target.value)}
                placeholder="Optional secondary reference"
              />
            </div>
            <div>
              <Label>Contact name</Label>
              <Input value={form.supplierContactName || ""} onChange={(e) => setField("supplierContactName", e.target.value)} />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input type="email" value={form.supplierEmail || ""} onChange={(e) => setField("supplierEmail", e.target.value)} />
            </div>
            <div>
              <Label>Contact phone</Label>
              <Input value={form.supplierPhone || ""} onChange={(e) => setField("supplierPhone", e.target.value)} />
            </div>

            {!form.confirmed && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                AI extracted these fields automatically. Please review and click Save to confirm.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-duffells-grey flex items-center gap-2">
              <Files className="w-4 h-4" />
              Documents
            </h3>
            <p className="text-xs text-duffells-grey/60 mt-1">
              The original contract PDF plus any supporting documents (amendments, signed copies, schedules).
            </p>
          </div>

          <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-duffells-grey truncate">{contract?.originalFilename}</div>
                  <div className="text-xs text-duffells-grey/50">Original contract</div>
                </div>
              </div>
              <a
                href={`${BASE}/api/contracts/${id}/preview`}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary hover:underline shrink-0"
              >
                View
              </a>
            </div>

            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-duffells-grey/50 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-duffells-grey truncate">{doc.originalFilename}</div>
                    <div className="text-xs text-duffells-grey/50">
                      Added {(() => { try { return format(parseISO(doc.createdAt), "d MMM yyyy"); } catch { return doc.createdAt; } })()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <a
                    href={`${BASE}/api/contract-documents/${doc.id}/preview`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    View
                  </a>
                  {isAdmin && (
                    <button
                      onClick={() => { if (confirm(`Remove "${doc.originalFilename}"?`)) deleteDocMutation.mutate(doc.id); }}
                      className="text-red-500 hover:text-red-700"
                      title="Remove document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div>
            <FileDropzone
              onUpload={handleDocFiles}
              accept=".pdf"
              label="Drop additional contract document here"
              multiple={true}
              maxSize={50 * 1024 * 1024}
            />
            {uploadingDoc && (
              <div className="mt-2 flex items-center gap-2 text-sm text-duffells-grey">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading…
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-semibold text-duffells-grey flex items-center gap-2">
                  <Wallet className="w-4 h-4" />
                  Prepayment
                </h3>
                <p className="text-xs text-duffells-grey/60 mt-1 max-w-xl">
                  Tick this when the contract is paid upfront and the cost spreads
                  across the contract's months on the P&amp;L. We'll auto-generate
                  the monthly release schedule and the Merlin journal CSV.
                </p>
              </div>
              {form.isPrepayment && prepaymentDetail?.summary && (
                <a
                  href={`${BASE.startsWith("/") ? BASE : ""}/prepayments/${id}`}
                  className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
                >
                  View schedule
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.isPrepayment}
                onChange={(e) => setField("isPrepayment", e.target.checked)}
                className="w-4 h-4 accent-duffells-orange"
              />
              <span className="text-sm text-duffells-grey font-medium">
                This contract is a prepayment
              </span>
            </label>

            {form.isPrepayment && (
              <div className="space-y-4 border-l-2 border-duffells-orange/30 pl-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Prepayment amount paid upfront</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.prepaymentAmount ?? ""}
                      onChange={(e) => setField("prepaymentAmount", e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="e.g. 12000.00"
                    />
                    <p className="text-xs text-duffells-grey/60 mt-1">
                      Released across {form.startDate && form.endDate ? "the months between start and end date" : "the contract's start–end date range"}.
                    </p>
                  </div>
                  <div>
                    <Label>Journal description (optional)</Label>
                    <Input
                      value={form.prepaymentDescription || ""}
                      onChange={(e) => setField("prepaymentDescription", e.target.value)}
                      placeholder={`Prepayment release: ${form.title || "—"}`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Override DR (P&amp;L) nominal code</Label>
                    <Input
                      value={form.prepaymentNominalDr || ""}
                      onChange={(e) => setField("prepaymentNominalDr", e.target.value)}
                      placeholder="Leave blank to use default"
                    />
                  </div>
                  <div>
                    <Label>Override CR (Prepayments) nominal code</Label>
                    <Input
                      value={form.prepaymentNominalCr || ""}
                      onChange={(e) => setField("prepaymentNominalCr", e.target.value)}
                      placeholder="Leave blank to use default"
                    />
                  </div>
                </div>

                {prepaymentDetail?.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-xs text-duffells-grey/60">Months</div>
                      <div className="font-semibold">
                        {prepaymentDetail.summary.monthsReleased}/{prepaymentDetail.summary.monthsTotal}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-xs text-duffells-grey/60">Released</div>
                      <div className="font-semibold">{formatCurrency(prepaymentDetail.summary.releasedAmount, form.currency || "GBP")}</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-xs text-duffells-grey/60">Pending</div>
                      <div className="font-semibold">{formatCurrency(prepaymentDetail.summary.pendingAmount, form.currency || "GBP")}</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <div className="text-xs text-amber-700">Remaining balance</div>
                      <div className="font-semibold text-amber-900">{formatCurrency(prepaymentDetail.summary.remainingAmount, form.currency || "GBP")}</div>
                    </div>
                  </div>
                )}

                {(!form.startDate || !form.endDate || !form.prepaymentAmount) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                    Set start date, end date, and prepayment amount to generate the release schedule.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
